import type { CalendarEvent, ConflictResult, ConflictRule, ConflictRuleContext, ConflictSummary } from './conflict.types';
import { isCalendarConflictType, isShiftOverlapConflictType, isCrossTeamConflictType, isSkillConflictType } from './conflict.types';
import type { ScheduleMap } from '../models/expanded-schedule.model';

/**
 * Runs all conflict rules against the expanded schedule and calendar events.
 * Deduplicates results by composite key: `personId + date + type`.
 *
 * For calendar conflicts (same type with multiple calendarIds), keeps the first
 * match by `calendarId` sort order.
 *
 * For overlap conflicts (which carry `shiftUnitId`), the dedup key includes
 * the shift unit pair so that two different overlaps for the same person on the
 * same date are preserved.
 *
 * @param schedule - Date-keyed schedule (from expandSchedule)
 * @param events - Calendar events with applicablePersonIds
 * @param rules - Conflict detection rules to apply
 * @param context - Optional context with additional metadata (e.g., shift unit time ranges)
 * @returns Deduplicated array of conflict results
 */
export function detectConflicts(
  schedule: ScheduleMap,
  events: CalendarEvent[],
  rules: ConflictRule[],
  context?: ConflictRuleContext
): ConflictResult[] {
  const allResults: ConflictResult[] = [];

  for (const rule of rules) {
    allResults.push(...rule.detect(schedule, events, context));
  }

  // Deduplicate: use a composite key that differentiates overlap pairs
  allResults.sort((a, b) => a.calendarId.localeCompare(b.calendarId));

  const seen = new Set<string>();
  const deduped: ConflictResult[] = [];

  for (const result of allResults) {
    const key = buildDedupKey(result);
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(result);
    }
  }

  return deduped;
}

/**
 * Build a deduplication key for a conflict result.
 *
 * Calendar conflicts: `personId:date:type` (same as before)
 * Overlap conflicts: `personId:date:type:sortedShiftUnitPair` so that
 * two different overlap pairs for the same person/date are preserved.
 */
function buildDedupKey(result: ConflictResult): string {
  const base = `${result.personId}:${result.date}:${result.type}`;
  if (result.shiftUnitId && result.conflictingShiftUnitId) {
    // Sort the pair so A-B and B-A produce the same key
    const pair = [result.shiftUnitId, result.conflictingShiftUnitId].sort((a, b) => a.localeCompare(b));
    return `${base}:${pair[0]}:${pair[1]}`;
  }
  return base;
}

/**
 * Computes a summary from a flat array of conflict results.
 * Returns unique person count and alphabetically-sorted unique event labels.
 *
 * @param conflicts - Flat array of ConflictResult (typically already deduplicated)
 * @returns ConflictSummary with affectedPersonCount and eventLabels
 */
export function summarizeConflicts(conflicts: ConflictResult[]): ConflictSummary {
  const uniquePersons = new Set<string>();
  const uniqueLabels = new Set<string>();
  let calendarConflictCount = 0;
  let overlapConflictCount = 0;
  let skillConflictCount = 0;
  let crossTeamConflictCount = 0;

  for (const c of conflicts) {
    uniquePersons.add(c.personId);
    uniqueLabels.add(c.eventLabel);
    if (isCalendarConflictType(c.type)) {
      calendarConflictCount += 1;
    } else if (isShiftOverlapConflictType(c.type)) {
      overlapConflictCount += 1;
    } else if (isSkillConflictType(c.type)) {
      skillConflictCount += 1;
    } else if (isCrossTeamConflictType(c.type)) {
      crossTeamConflictCount += 1;
    }
  }

  return {
    affectedPersonCount: uniquePersons.size,
    totalConflictCount: conflicts.length,
    calendarConflictCount,
    overlapConflictCount,
    skillConflictCount,
    crossTeamConflictCount,
    eventLabels: [...uniqueLabels].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Groups conflict results into week buckets by date.
 * Used by the backend batch endpoint to pre-group results before returning.
 *
 * @param conflicts - Flat array of ConflictResult
 * @param weeks - Array of week boundaries with YYYY-MM-DD strings
 * @returns Record keyed by weekStart string, each containing ConflictResult[] for that week
 */
export function groupConflictsByWeek(
  conflicts: ConflictResult[],
  weeks: { weekStart: string; weekEnd: string }[]
): Record<string, ConflictResult[]> {
  const result: Record<string, ConflictResult[]> = {};

  for (const conflict of conflicts) {
    for (const week of weeks) {
      if (conflict.date >= week.weekStart && conflict.date <= week.weekEnd) {
        if (!result[week.weekStart]) {
          result[week.weekStart] = [];
        }
        result[week.weekStart].push(conflict);
        break; // Each conflict belongs to exactly one week
      }
    }
  }

  return result;
}
